import React from 'react'
import PropTypes from 'prop-types'
import { reduxForm, Field, formValues } from 'redux-form'
import ConfigureInfo from './configure-info'
import ConfigureFiltering from './configure-filtering'
import ConfigureTriggersAndActions from './configure-triggers-actions'
import ConfigureModeration from './configure-moderation'
import ConfigureOwnership from './configure-ownership'
import ConfigureRateLimits from './configure-rate-limits'
import { ConfigureUniqueCodes } from './configure-unique-codes'
import ConfigureMosaicDeployment from './configure-mosaic-deployment'
import ConfigureScoreboard from './configure-scoreboard'
import ConfigureThreadVenture from './configure-threadventure'
import Status from './status'
import { notification as notify } from 'utils'
import { propOr, pathOr } from 'ramda'
import { SelectPickerField } from 'components/form/selectpicker'
import { SPEED_THREAD_START_FORM_TYPE } from '../mapping/actions/twitter/widget-to-form/start-speed-thread'
import { SPEED_THREAD_STOP_FORM_TYPE } from '../mapping/actions/twitter/widget-to-form/stop-speed-thread'
import { SPEED_THREAD_RESET_FORM_TYPE } from '../mapping/actions/twitter/widget-to-form/reset-speed-thread'

const StatusField = formValues({ startDate: 'startDate', endDate: 'endDate' })(
  Status
)

// Form action types that mark a widget as a speed thread. Imported from the
// converters that define them so a rename can't silently break detection.
const SPEED_THREAD_ACTION_TYPES = [
  SPEED_THREAD_START_FORM_TYPE,
  SPEED_THREAD_STOP_FORM_TYPE,
  SPEED_THREAD_RESET_FORM_TYPE
]

// A saved speed thread opens on the mention edit route with no scoreboardConfig
// until one is added. Detect it by its actions (in actions or draftActions) so
// the scoreboard section renders regardless of an existing config.
const hasSpeedThreadActions = initialValues =>
  [
    ...pathOr([], ['actions'], initialValues),
    ...pathOr([], ['draftActions'], initialValues)
  ].some(set =>
    (set.success || []).some(action =>
      SPEED_THREAD_ACTION_TYPES.includes(action.type)
    )
  )

export const MentionsForm = ({
  widgetId,
  handleSubmit,
  submitting,
  pristine,
  status,
  onSubmit,
  error,
  experiencePlatform,
  experienceType,
  change,
  initialValues,
  showMosaicDeployModal,
  handleShowMosaicDeployModal,
  handleHideMosaicDeployModal
}) => {
  if (error) {
    notify.error({
      title: 'Validation Error',
      message: error
    })
  }

  const codeList = propOr(null, 'codeList')(initialValues)
  const mosaicCampaignId = pathOr(null, ['mosaic', 'campaignId'], initialValues)
  const notificationFrequency = propOr(
    '',
    'notificationFrequency'
  )(initialValues)
  // Speed thread widgets are type MENTION, so a saved one opens via the mention
  // edit route (experienceType 'mentions'), not the speed thread route. Show the
  // scoreboard section there too when the widget already carries a config, so the
  // saved data isn't hidden on edit.
  const hasScoreboardConfig = Boolean(
    pathOr(null, ['scoreboardConfig'], initialValues)
  )

  const isSpeedThreadWidget = hasSpeedThreadActions(initialValues)

  if (experienceType === 'threadVenture') {
    return (
      <div className='form'>
        <form
          className='form-horizontal'
          onSubmit={handleSubmit(onSubmit(false))}
        >
          <div className='form-body'>
            <Field name='type' component='input' type='hidden' />
            <Field name='status' component={StatusField} />
            <ConfigureInfo />
            <ConfigureOwnership experiencePlatform={experiencePlatform} />
          </div>
          <ConfigureThreadVenture />
          <div className='form-actions'>
            <div style={{ float: 'right' }}>
              <button
                id='brt-experience-save'
                className='btn btn green btn-outline'
                disabled={pristine || submitting}
              >
                Save
              </button>
              <button
                type='button'
                id='brt-experience-save-start'
                className='btn btn green-haze'
                onClick={handleSubmit(onSubmit(true))}
                disabled={pristine || submitting}
              >
                Save and start
              </button>
            </div>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className='form'>
      <form
        className='form-horizontal'
        onSubmit={handleSubmit(onSubmit(false))}
      >
        <div className='form-body'>
          {mosaicCampaignId && (
            <div>
              <button
                type='button'
                className='btn green-haze'
                style={{ marginBottom: '50px' }}
                onClick={handleShowMosaicDeployModal}
                id='deploy-mosaic-button'
              >
                <i className='fa fa-rocket' />
                Deploy Mosaic
              </button>
              <br />
              <span className='caption-subject bold font-green-haze uppercase'>
                {`Mosaic Campaign ID: ${mosaicCampaignId}`}
              </span>
              <br />
            </div>
          )}
          {widgetId && (
            <div>
              <button
                type='button'
                className='btn green-haze'
                style={{ marginBottom: '50px' }}
                onClick={() =>
                  (window.location.href = `/participants/${widgetId}`)
                }
                id='view-participants-button'
              >
                <i className='fa fa-eye' />
                View Participants
              </button>
              <br />
              <span className='caption-subject bold font-green-haze uppercase'>
                {`Mosaic Campaign ID: ${mosaicCampaignId}`}
              </span>
              <br />
            </div>
          )}
          {showMosaicDeployModal && (
            <ConfigureMosaicDeployment
              showMosaicDeployModal={showMosaicDeployModal}
              handleShowMosaicDeployModal={handleShowMosaicDeployModal}
              handleHideMosaicDeployModal={handleHideMosaicDeployModal}
              widgetId={widgetId}
              campaignId={mosaicCampaignId}
            />
          )}
          {experienceType === 'facebook_recurring_reminder' && (
            <div className='form-group'>
              <label className='col-sm-2 control-label'>
                Notification frequency
              </label>
              <div className='col-sm-6 col-md-5'>
                <Field
                  name='notificationFrequency'
                  component={SelectPickerField}
                  defaultValue={notificationFrequency}
                  data-size='10'
                  className='form-control'
                >
                  <option value='DAILY'>Daily</option>
                  <option value='WEEKLY'>Weekly</option>
                  <option value='MONTHLY'>Monthly</option>
                </Field>
              </div>
            </div>
          )}
          <Field name='type' component='input' type='hidden' />
          <Field name='status' component={StatusField} />
          <ConfigureInfo />
          <ConfigureOwnership experiencePlatform={experiencePlatform} />
          {experiencePlatform === 'twitter' && <ConfigureRateLimits />}
          <ConfigureTriggersAndActions
            experiencePlatform={experiencePlatform}
          />
          {experiencePlatform === 'twitter' && <ConfigureModeration />}
          {(experienceType === 'twitter_speed_thread' ||
            isSpeedThreadWidget ||
            hasScoreboardConfig) && <ConfigureScoreboard />}
          {(experienceType === 'mentions' ||
            experienceType === 'instagram_coupon' ||
            experienceType === 'fb_coupon') && (
            <ConfigureUniqueCodes updateReduxField={change} listId={codeList} />
          )}
          {experiencePlatform === 'twitter' && (
            <ConfigureFiltering widgetId={widgetId} />
          )}
        </div>

        <div className='form-actions'>
          <div style={{ float: 'right' }}>
            <button
              id='brt-experience-save'
              className='btn btn green btn-outline'
              disabled={pristine || submitting}
            >
              Save
            </button>
            <button
              type='button'
              id='brt-experience-save-start'
              className='btn btn green-haze'
              onClick={handleSubmit(onSubmit(true))}
              disabled={pristine || submitting}
            >
              Save and start
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

MentionsForm.propTypes = {
  widgetId: PropTypes.string,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  pristine: PropTypes.bool,
  status: PropTypes.string,
  onSubmit: PropTypes.func,
  error: PropTypes.string,
  experiencePlatform: PropTypes.string,
  experienceType: PropTypes.string,
  change: PropTypes.func,
  initialValues: PropTypes.object,
  handleShowMosaicDeployModal: PropTypes.func,
  handleHideMosaicDeployModal: PropTypes.func,
  showMosaicDeployModal: PropTypes.bool
}

export default reduxForm({
  form: 'widget',
  initialValues: {
    type: 'MENTION',
    status: 'draft',
    startDate: new Date().valueOf(),
    endDate: new Date().valueOf(),
    actions: [
      {
        platform: 'twitter',
        description: 'Trigger and Action set #1',
        operator: 'all',
        triggers: [],
        success: [],
        failure: []
      }
    ],
    moderated: false,
    moderators: { handles: [] },
    automated: false,
    threshold: 0,
    enableFiltering: true,
    enableBlastFiltering: true,
    store: false,
    profanity: false,
    lock: false
  },
  enableReinitialize: true
})(MentionsForm)



// WEBPACK FOOTER //
// ./src/pages/widgets/form/index.js